// biome-ignore-all format: balance tables are kept one row per entry, aligned for comparison
/**
 * Balance tables. Every number that shapes gameplay lives here so it can be tuned in one place
 * (and by headless AI-vs-AI simulations, see scripts/simulate.ts).
 */
import type { Season, TechBranch, TechId, UnitType } from './types';
import { Terrain } from './types';

/** Ticks per in-game day. */
export const HOURS_PER_DAY = 24;
/** Hour of day at tick 0. */
export const START_HOUR = 6;
/** Night = hours in [NIGHT_START, 24) ∪ [0, NIGHT_END). */
export const NIGHT_START = 20;
export const NIGHT_END = 5;

export const isNight = (tick: number): boolean => {
  const h = (tick + START_HOUR) % HOURS_PER_DAY;
  return h >= NIGHT_START || h < NIGHT_END;
};
export const hourOfDay = (tick: number): number => (tick + START_HOUR) % HOURS_PER_DAY;
export const dayOf = (tick: number): number => Math.floor((tick + START_HOUR) / HOURS_PER_DAY) + 1;

/** Max friendly units per hex. */
export const STACK_LIMIT = 3;

export interface UnitStats {
  /** Soft attack value (per full-strength unit). */
  attack: number;
  defense: number;
  /** Hours to cross one plains hex off-road. */
  hoursPerHex: number;
  /** Multiplier applied to hoursPerHex on roads. */
  roadFactor: number;
  /** Vision radius in hexes. */
  sight: number;
  /** Detection level this unit achieves on enemies within its sight (beyond adjacency). */
  spotLevel: 1 | 2;
  /** Artillery-style fire support range (0 = none). */
  supportRange: number;
  /** Supply draw relative to infantry. */
  supplyUse: number;
  /** Armoured units suffer in close terrain and are resistant to soft attack. */
  armored: boolean;
  motorized: boolean;
  cost: { industry: number; manpower: number };
  /** Can be produced by players. */
  buildable: boolean;
  /** Tech required to build (if any). */
  requires: TechId | null;
}

export const UNIT_STATS: Record<UnitType, UnitStats> = {
  infantry: {
    attack: 10, defense: 14, hoursPerHex: 6, roadFactor: 0.6, sight: 2, spotLevel: 1, supportRange: 0,
    supplyUse: 1, armored: false, motorized: false, cost: { industry: 40, manpower: 10 }, buildable: true, requires: null,
  },
  motorized: {
    attack: 12, defense: 13, hoursPerHex: 3.5, roadFactor: 0.45, sight: 2, spotLevel: 1, supportRange: 0,
    supplyUse: 1.5, armored: false, motorized: true, cost: { industry: 70, manpower: 10 }, buildable: true, requires: 'motorization',
  },
  armor: {
    attack: 22, defense: 11, hoursPerHex: 3, roadFactor: 0.5, sight: 2, spotLevel: 1, supportRange: 0,
    supplyUse: 2, armored: true, motorized: true, cost: { industry: 120, manpower: 6 }, buildable: true, requires: null,
  },
  artillery: {
    attack: 16, defense: 6, hoursPerHex: 7, roadFactor: 0.55, sight: 2, spotLevel: 1, supportRange: 2,
    supplyUse: 1.5, armored: false, motorized: false, cost: { industry: 60, manpower: 5 }, buildable: true, requires: null,
  },
  recon: {
    attack: 5, defense: 5, hoursPerHex: 2.2, roadFactor: 0.6, sight: 4, spotLevel: 2, supportRange: 0,
    supplyUse: 0.6, armored: false, motorized: true, cost: { industry: 30, manpower: 3 }, buildable: true, requires: null,
  },
  hq: {
    attack: 2, defense: 4, hoursPerHex: 4, roadFactor: 0.5, sight: 2, spotLevel: 1, supportRange: 0,
    supplyUse: 0.5, armored: false, motorized: true, cost: { industry: 80, manpower: 2 }, buildable: true, requires: null,
  },
  decoy: {
    attack: 0, defense: 1, hoursPerHex: 5, roadFactor: 0.6, sight: 1, spotLevel: 1, supportRange: 0,
    supplyUse: 0.1, armored: false, motorized: false, cost: { industry: 12, manpower: 1 }, buildable: true, requires: 'deception',
  },
};

export interface TerrainStats {
  passable: boolean;
  /** Movement cost multiplier (plains = 1). */
  moveCost: number;
  /** Defender bonus multiplier. */
  defense: number;
  /** Extra attack multiplier for armoured attackers (close terrain hurts tanks). */
  armorAttack: number;
  /** Units in this terrain are only seen from adjacent hexes unless spotter is recon. */
  concealing: boolean;
  /** Extra sight radius for units standing here. */
  sightBonus: number;
  /** Supply propagation cost multiplier. */
  supplyCost: number;
}

export const TERRAIN_STATS: Record<number, TerrainStats> = {
  [Terrain.Ocean]: { passable: false, moveCost: 99, defense: 1, armorAttack: 1, concealing: false, sightBonus: 0, supplyCost: 99 },
  [Terrain.Shallows]: { passable: false, moveCost: 99, defense: 1, armorAttack: 1, concealing: false, sightBonus: 0, supplyCost: 99 },
  [Terrain.Lake]: { passable: false, moveCost: 99, defense: 1, armorAttack: 1, concealing: false, sightBonus: 0, supplyCost: 99 },
  [Terrain.Plains]: { passable: true, moveCost: 1, defense: 1, armorAttack: 1.15, concealing: false, sightBonus: 0, supplyCost: 1 },
  [Terrain.Forest]: { passable: true, moveCost: 1.6, defense: 1.35, armorAttack: 0.7, concealing: true, sightBonus: 0, supplyCost: 1.4 },
  [Terrain.Hills]: { passable: true, moveCost: 1.7, defense: 1.5, armorAttack: 0.8, concealing: false, sightBonus: 1, supplyCost: 1.5 },
  [Terrain.Mountains]: { passable: true, moveCost: 3, defense: 2.2, armorAttack: 0.5, concealing: true, sightBonus: 2, supplyCost: 2.5 },
  [Terrain.Marsh]: { passable: true, moveCost: 2.5, defense: 1.25, armorAttack: 0.55, concealing: false, sightBonus: 0, supplyCost: 2 },
  [Terrain.Urban]: { passable: true, moveCost: 1.2, defense: 1.8, armorAttack: 0.6, concealing: true, sightBonus: 0, supplyCost: 0.8 },
};

/** Extra movement multiplier when crossing a river edge (no bridge = road on that edge). */
export const RIVER_CROSSING_MOVE = 1.8;
/** Attack multiplier when attacking across a river edge. */
export const RIVER_CROSSING_ATTACK = 0.7;
export const NIGHT_MOVE = 1.3;
export const NIGHT_ATTACK = 0.8;
/** Attack bonus per additional attack direction beyond the first (pincer / flanking). */
export const FLANK_BONUS = 0.15;
export const MAX_FLANK_BONUS = 0.45;

// ---- command & control ----------------------------------------------------------------------

/** Base radius (hexes) around an HQ within which orders go by field telephone. */
export const COMMAND_RADIUS = 3;
/** Delay (ticks) for telephone orders inside command radius. */
export const TELEPHONE_DELAY = 1;
/** Hours per hex for a mounted courier off-road / on-road. */
export const COURIER_HOURS_PER_HEX = { rider: 1.6, motorcycle: 0.9 } as const;
export const COURIER_ROAD_FACTOR = 0.55;
/** Base chance a courier is captured per hex step spent adjacent to a hostile unit. */
export const COURIER_CAPTURE_CHANCE = 0.35;
/** Base chance for an enemy with codebreaking to decrypt one radio order. */
export const RADIO_DECRYPT_CHANCE = { codebreaking: 0.25, codebreaking2: 0.5 } as const;
/** Encryption reduces decrypt chance by this factor. */
export const ENCRYPTION_FACTOR = 0.4;
/** Intercepted orders stay on the map this many ticks. */
export const INTERCEPT_TTL = 36;
/** Orders with no HQ alive are sent from the capital with this extra delay. */
export const NO_HQ_EXTRA_DELAY = 6;

// ---- intel ----------------------------------------------------------------------------------

/** Remembered contacts are forgotten after this many ticks without sighting. */
export const INTEL_MEMORY = 96;

// ---- logistics -------------------------------------------------------------------------------

/** Supply budget a source pushes out (cost units). */
export const SUPPLY_RANGE = { capital: 14, city: 8, town: 5 } as const;
export const ROAD_SUPPLY_FACTOR = 0.5;
export const RAIL_SUPPLY_FACTOR = 0.25;
/** Below this supply a unit counts as out of supply. */
export const LOW_SUPPLY = 0.25;
/** Hours at zero supply after which units with collapsed org surrender. */
export const SURRENDER_AFTER_HOURS = 48;

// ---- recovery --------------------------------------------------------------------------------

export const ORG_RECOVERY_PER_HOUR = 0.02;
export const STRENGTH_RECOVERY_PER_HOUR = 0.004;
export const ENTRENCH_PER_HOUR = 0.02;

// ---- economy -------------------------------------------------------------------------------

/** Base research points per hour for every living faction. */
export const BASE_RESEARCH = 0.6;
/** Research points per point of industry per hour. */
export const RESEARCH_PER_INDUSTRY = 0.12;
/** Industry points per hour are city industry/day divided by 24. */
export const MAX_PARALLEL_PRODUCTION = 4;

export interface TechDef {
  id: TechId;
  name: string;
  branch: TechBranch;
  cost: number;
  requires: TechId[];
  description: string;
}

export const TECHS: Record<TechId, TechDef> = {
  field_telephone: { id: 'field_telephone', name: 'Field Telephone Net', branch: 'signals', cost: 60, requires: [], description: 'Command radius +2 hexes around every HQ.' },
  motor_couriers: { id: 'motor_couriers', name: 'Motorcycle Couriers', branch: 'signals', cost: 70, requires: [], description: 'Couriers travel ~75% faster.' },
  radio: { id: 'radio', name: 'Field Radio', branch: 'signals', cost: 140, requires: ['field_telephone'], description: 'Orders may be sent by radio: near-instant, but enemy codebreakers can read them.' },
  encryption: { id: 'encryption', name: 'Rotor Cipher', branch: 'signals', cost: 120, requires: ['radio'], description: 'Enemy decryption chance on your radio traffic reduced by 60%.' },
  codebreaking: { id: 'codebreaking', name: 'Cryptanalysis Bureau', branch: 'intelligence', cost: 90, requires: [], description: 'Chance to decrypt enemy radio orders.' },
  codebreaking2: { id: 'codebreaking2', name: 'Bombe Machines', branch: 'intelligence', cost: 160, requires: ['codebreaking'], description: 'Greatly improved decryption of enemy radio orders.' },
  aerial_recon: { id: 'aerial_recon', name: 'Aerial Reconnaissance', branch: 'intelligence', cost: 110, requires: [], description: 'Recon units +2 sight; daily photo sweeps reveal enemy positions near your front.' },
  deception: { id: 'deception', name: 'Deception Units', branch: 'intelligence', cost: 60, requires: [], description: 'Build decoy divisions that pose as real formations until assessed at close range.' },
  motorization: { id: 'motorization', name: 'Motorization', branch: 'warfare', cost: 90, requires: [], description: 'Unlock motorized infantry divisions.' },
  armor2: { id: 'armor2', name: 'Medium Tanks', branch: 'warfare', cost: 150, requires: ['motorization'], description: 'Armor attack +25%.' },
  heavy_artillery: { id: 'heavy_artillery', name: 'Heavy Artillery', branch: 'warfare', cost: 110, requires: [], description: 'Artillery support range +1 and attack +20%.' },
  combined_arms: { id: 'combined_arms', name: 'Combined Arms Doctrine', branch: 'warfare', cost: 130, requires: ['armor2'], description: '+20% attack when a battle includes armor and infantry on the attacking side.' },
  defense_in_depth: { id: 'defense_in_depth', name: 'Defence in Depth', branch: 'warfare', cost: 90, requires: [], description: 'Entrench twice as fast; +15% defence when entrenched.' },
  railways: { id: 'railways', name: 'Military Railways', branch: 'logistics', cost: 80, requires: [], description: 'Supply range +40% along rails and roads.' },
  field_hospitals: { id: 'field_hospitals', name: 'Field Hospitals', branch: 'logistics', cost: 70, requires: [], description: 'Units recover strength twice as fast and use 30% less manpower.' },
};

/** Muted "staff map" faction colours (index = default for faction id). */
export const FACTION_COLORS = [0xb23a2e, 0x2f5d8a, 0x5b7a3a, 0xc2892c, 0x6d4a7d, 0x2f7f7a] as const;

export const MAP_SIZES = {
  small: { width: 48, height: 34 },
  medium: { width: 64, height: 44 },
  large: { width: 84, height: 56 },
} as const;

// =============================================================================================
// Simulation tuning (see docs/Engine.md for the formulas these feed into)
// =============================================================================================

// ---- orders & movement ----------------------------------------------------------------------

/** Delay (ticks) for radio orders. */
export const RADIO_DELAY = 1;
/** Max units addressed by one order command. */
export const MAX_ORDER_UNITS = 64;
/** Moving between two hexes that are both adjacent to hostile units (zone of control). */
export const ZOC_MOVE = 1.5;
/** Movement slowdown for units below LOW_SUPPLY / below LOW_ORG. */
export const LOW_SUPPLY_MOVE = 1.4;
export const LOW_ORG = 0.3;
export const LOW_ORG_MOVE = 1.3;
/** Path planning: extra hours for crossing a hex with a known enemy contact (attack orders). */
export const PLAN_CONTACT_PENALTY = 40;
/** Path planning: extra cost (× hoursPerHex) for hexes next to known contacts (move/retreat orders). */
export const PLAN_ZOC_PENALTY = 1;
/** Courier routing: extra hours for hexes next to known contacts (risk of capture). */
export const COURIER_RISK_PENALTY = 4;
/** Hard cap on A* expansions per search (protects the tick budget on huge detours). */
export const MAX_PATH_EXPANSIONS = 8000;

// ---- combat ---------------------------------------------------------------------------------

/** Organisation lost per hour by each unit of a side at power ratio 1:1. */
export const ORG_DAMAGE = 0.04;
/** Org loss scales with (power ratio)^COMBAT_EXPONENT. */
export const COMBAT_EXPONENT = 0.75;
/** Log-normal sigma of the hourly power-ratio noise. */
export const COMBAT_VARIANCE = 0.25;
export const MAX_ORG_LOSS_PER_HOUR = 0.3;
/** Strength lost as a fraction of org lost (defender / attacker). */
export const STRENGTH_LOSS_DEF = 0.22;
export const STRENGTH_LOSS_ATT = 0.28;
/** Units at or below this strength are destroyed. */
export const MIN_STRENGTH = 0.05;
/** Fraction of an idle artillery unit's attack added to a battle within its support range. */
export const ARTILLERY_SUPPORT = 0.5;
/** Full entrenchment multiplies defence by 1 + ENTRENCH_DEFENSE. */
export const ENTRENCH_DEFENSE = 0.5;
export const DEFENSE_IN_DEPTH_BONUS = 1.15;
export const ARMOR2_ATTACK = 1.25;
export const HEAVY_ARTILLERY_ATTACK = 1.2;
export const COMBINED_ARMS_ATTACK = 1.2;
/** Combat multiplier at experience 1 is 1 + EXPERIENCE_BONUS. */
export const EXPERIENCE_BONUS = 0.3;
export const EXPERIENCE_PER_BATTLE_HOUR = 0.01;
/** Defender org at which it falls back (by posture); units under a hold order fight on to HOLD_BREAK_ORG. */
export const DEFENDER_BREAK_ORG = { aggressive: 0.1, balanced: 0.15, defensive: 0.35 } as const;
export const HOLD_BREAK_ORG = 0.05;
/** Attacker org at which the assault is called off (by posture). */
export const ATTACKER_BREAK_ORG = { aggressive: 0.15, balanced: 0.2, defensive: 0.3 } as const;
/** Below ROUTED_ORG a unit that falls back is routed; it rallies at RALLY_ORG. */
export const ROUTED_ORG = 0.15;
export const RALLY_ORG = 0.3;
/** Org cost of disengaging: retreat order from a defended hex / calling off an attack. */
export const RETREAT_ORG_PENALTY = 0.1;
export const BREAK_OFF_ORG_PENALTY = 0.05;
/** Aggressive idle units attack adjacent enemies if their estimated power ratio reaches this. */
export const AGGRESSIVE_ATTACK_RATIO = 1.5;
export const AGGRESSIVE_MIN_ORG = 0.6;

// ---- control --------------------------------------------------------------------------------

/** Per-tick chance that an empty, uncontested hostile hex next to your units flips to you. */
export const CONTROL_FLIP_CHANCE = 0.35;
/** Per supply update chance that an edge hex of an abandoned, unsupplied enclave is mopped up. */
export const MOPUP_CHANCE = 0.5;

// ---- supply ---------------------------------------------------------------------------------

/** Supply networks are recomputed every SUPPLY_INTERVAL ticks. */
export const SUPPLY_INTERVAL = 2;
/** Hexes with at least this much remaining supply budget are fully supplied; linear below. */
export const SUPPLY_FALLOFF = 3;
/** A unit is fully supplied when hex supply ≥ SUPPLY_NEED × its supplyUse. */
export const SUPPLY_NEED = 0.4;
/** Cities inside a pocket push only this fraction of their normal supply budget. */
export const POCKET_SOURCE_FACTOR = 0.5;
/** Supply cost multiplier across a river edge without road/rail bridge. */
export const RIVER_SUPPLY_FACTOR = 1.5;
/** Military Railways: road/rail supply costs divided by this. */
export const RAILWAYS_SUPPLY_BONUS = 1.4;
/** Hourly org / strength attrition at zero unit supply (scaled linearly up to LOW_SUPPLY). */
export const OOS_ORG_DECAY = 0.02;
export const OOS_STRENGTH_DECAY = 0.001;
/** Unit supply below this counts as "zero" for SURRENDER_AFTER_HOURS. */
export const ZERO_SUPPLY = 0.05;
/** Starved units surrender once their org is below this. */
export const SURRENDER_ORG = 0.1;

// ---- recovery -------------------------------------------------------------------------------

/** Field Hospitals: manpower cost multiplier for reinforcements. */
export const FIELD_HOSPITAL_MANPOWER = 0.7;
/** Unit supply needed to receive replacements. */
export const REINFORCE_MIN_SUPPLY = 0.5;
/** Entrenchment level at which a stationary unit shows as 'entrenched'. */
export const ENTRENCHED_LEVEL = 0.5;

// ---- vision & intel -------------------------------------------------------------------------

/** Sight radius of controlled cities by size (index = size). */
export const CITY_SIGHT = [0, 1, 2, 2] as const;
/** Aerial Reconnaissance: recon sight bonus, daily photo sweep hour and range around own units. */
export const AERIAL_RECON_SIGHT = 2;
export const AERIAL_SWEEP_HOUR = 12;
export const AERIAL_SWEEP_RANGE = 3;
/** Max 'contact' events per faction per tick (keeps the teletype readable). */
export const CONTACT_EVENTS_PER_TICK = 3;

// ---- economy --------------------------------------------------------------------------------

/** An item absorbs at most cost / MIN_PRODUCTION_HOURS industry per hour. */
export const MIN_PRODUCTION_HOURS = 24;
export const MAX_PRODUCTION_QUEUE = 12;
/** Industry and manpower of cities that did not start in your territory. */
export const OCCUPIED_CITY_FACTOR = 0.5;
export const STARTING_MANPOWER = 60;
/** Starting army per faction (plus one Army HQ): a front line, a mobile reserve, a capital garrison. */
export const STARTING_ARMY = { frontInfantry: 5, reserveInfantry: 2, armor: 2, artillery: 1, recon: 1 } as const;

// ---- victory --------------------------------------------------------------------------------

/** Capital lost and VP below this share of the starting VP ⇒ capitulation. */
export const CAPITULATION_VP_SHARE = 0.25;
/** Consecutive hours a team must hold vpVictoryShare of all VP. */
export const VP_HOLD_HOURS = 24;
/**
 * The VP-victory clock cannot start before this tick: in a duel both sides begin near 50 %, and one
 * border metropolis lost on day 1 must not decide the war before anyone could react.
 */
export const VP_CLOCK_START = 72;

// ---- hosting --------------------------------------------------------------------------------

/** AI think interval (ticks) by difficulty. */
export const AI_THINK_INTERVAL = { recruit: 8, veteran: 4, general: 3 } as const;
/** ViewStream re-sends the full supply array at least this often (ticks). */
export const SUPPLY_RESEND_TICKS = 6;
/** Ticks of event history kept so a ViewStream never misses events between deltas. */
export const EVENT_HISTORY_TICKS = 48;

// ---- map generation -------------------------------------------------------------------------

/** Yields of a city by size (index = City.size: 1 town, 2 city, 3 metropolis); written by mapgen. */
export const CITY_YIELDS = [
  { vp: 0, industry: 0, manpower: 0 },
  { vp: 1, industry: 6, manpower: 1 },
  { vp: 3, industry: 14, manpower: 2 },
  { vp: 6, industry: 26, manpower: 4 },
] as const;
/** Extra victory points of a faction's starting capital. */
export const CAPITAL_VP_BONUS = 4;

// ---- weather & seasons (docs/Weather.md) ---------------------------------------------------

/** Precipitation (0..1) from which it counts as rain/snow, and as a storm/blizzard. */
export const RAIN_MIN = 0.2;
export const STORM_MIN = 0.65;
/** Precipitation of a cell's fringe that already means low cloud: no photo sweep below it. */
export const CLOUD_MIN = 0.08;
/** Ground wetness (0..1) from which the ground reads wet (cosmetic) and turns to mud. */
export const WET_MIN = 0.3;
export const MUD_MIN = 0.62;
/** Snow depth (0..1) from which a hex counts as snow-covered. */
export const SNOW_COVER_MIN = 0.12;
/** Precipitation falls as snow at or below this hex temperature (°C). */
export const SNOW_TEMP = 0.5;
/** Elevation of sea level and the lapse rate: the highest peaks (255) are LAPSE_RATE °C colder. */
export const SEA_LEVEL_ELEVATION = 64;
export const LAPSE_RATE = 9;

/** Step-cost multipliers. Precipitation applies on and off road; ground only off road. */
export const WEATHER_MOVE = {
  rain: 1.2,
  storm: 1.4,
  /** Mud, off-road: foot units / motorised & armoured units. */
  mudFoot: 1.4,
  mudMotor: 2.0,
  /** Snow cover, off-road. */
  snow: 1.3,
} as const;
/** Attack multipliers by the conditions of the defended hex. */
export const WEATHER_ATTACK = { rain: 0.9, storm: 0.8, mudArmor: 0.8, fog: 1.1 } as const;
/** Sight radius reduction for a spotter standing in rain/snow, a storm, fog (radius min 1). */
export const WEATHER_SIGHT = { rain: 1, storm: 1, fog: 2 } as const;
/** Couriers: speed factor in storms; off-road step multiplier in mud/snow; capture chance ×FOG. */
export const STORM_COURIER_SPEED = 0.7;
export const MUD_COURIER_MOVE = 1.3;
export const FOG_CAPTURE_FACTOR = 0.5;
/** Supply cost multiplier on off-road edges into mud. */
export const MUD_SUPPLY_COST = 1.3;
/** Out-of-supply attrition multiplier in snow (falling or lying). */
export const SNOW_ATTRITION = 1.5;

/** Ground model, per hour: wetting per unit of rain, snow per unit of snowfall, melt per °C. */
export const WET_GAIN = 0.055;
export const SNOW_GAIN = 0.06;
export const MELT_RATE = 0.006;
/** Base drying per hour (× terrain × warmth × sun × (0.6 + wetness)), and terrain drainage factors. */
export const DRY_RATE = 0.005;
export const TERRAIN_DRYING: Record<number, number> = {
  [Terrain.Plains]: 1,
  [Terrain.Forest]: 0.55,
  [Terrain.Hills]: 1.4,
  [Terrain.Mountains]: 1.8,
  [Terrain.Marsh]: 0.4,
  [Terrain.Urban]: 2,
};
/** Frost accumulates per °C-hour below zero and thaws per °C-hour above; frozen from 1, thawed below ½. */
export const FREEZE_RATE = 0.012;
export const THAW_RATE = 0.01;
export const FROST_MAX = 1.5;

/** Morning fog: hours (of day) it may lie, and the potential × strength it needs per hour (lifting). */
export const FOG_START_HOUR = 5;
/** The night's fog strength is settled (drawn) at this hour of the evening before. */
export const FOG_DRAW_HOUR = 18;
export const FOG_THRESHOLD = [0.3, 0.33, 0.4, 0.5] as const;

/** Temperature anomaly AR(1): persistence per hour and hourly noise (stationary sd ≈ 2.5 °C). */
export const ANOMALY_PERSISTENCE = 0.99;
export const ANOMALY_NOISE = 0.35;

/** Meteorological Service: bulletin every FORECAST_INTERVAL h, outlook steps, ensemble size. */
export const FORECAST_INTERVAL = 6;
export const FORECAST_LEADS = [3, 6, 9, 12, 15, 18, 21, 24] as const;
export const FORECAST_MEMBERS = 8;
/** Forecast error: drift velocity noise (hex units per hour) per member. */
export const FORECAST_DRIFT_ERROR = 0.09;
/** Sample hexes per region the forecast looks at. */
export const FORECAST_SAMPLES = 7;
/** MET warnings to a faction: at most one per MET_INTERVAL h, the same one not within 24 h. */
export const MET_INTERVAL = 12;
/** A MET warning needs this chance of precipitation / of a storm, or of morning fog. */
export const MET_PRECIP_CHANCE = 70;
export const MET_SEVERE_CHANCE = 50;
export const MET_FOG_CHANCE = 60;

/** Shares of land for the public ground notices (on / off, hysteresis). */
export const REGIME_SHARES = {
  frozen: [0.4, 0.15],
  mud: [0.2, 0.07],
  snow: [0.35, 0.1],
} as const;

export interface SeasonClimate {
  /** Sea-level mean temperature at the start (°C), its trend per day and half the day–night swing. */
  temp: number;
  trend: number;
  diurnal: number;
  /** Prevailing wind speed (hex units per hour). */
  wind: number;
  /** Mean number of frontal systems per 1000 map hexes (spawn rate follows from their life). */
  fronts: number;
  /** Front semi-axes (across / along the motion), peak intensity and lifetime ranges. */
  frontMajor: readonly [number, number];
  frontMinor: readonly [number, number];
  frontPeak: readonly [number, number];
  frontLife: readonly [number, number];
  /** Afternoon (12–17 h) convective storms: expected new cells per hour per 1000 hexes. */
  convective: number;
  convRadius: readonly [number, number];
  convPeak: readonly [number, number];
  convLife: readonly [number, number];
  /** Chance that a given morning is foggy. */
  fogChance: number;
  /** Initial ground: wetness on average ground, snow depth, frozen. */
  wetStart: number;
  snowStart: number;
  frozenStart: boolean;
}

export const CLIMATE: Record<Season, SeasonClimate> = {
  spring: {
    temp: 7, trend: 0.25, diurnal: 5, wind: 0.5,
    fronts: 1.2, frontMajor: [8, 15], frontMinor: [3.5, 6], frontPeak: [0.45, 0.9], frontLife: [24, 48],
    convective: 0.05, convRadius: [2.5, 4], convPeak: [0.65, 0.95], convLife: [4, 8],
    fogChance: 0.35, wetStart: 0.45, snowStart: 0, frozenStart: false,
  },
  summer: {
    temp: 21, trend: 0, diurnal: 6, wind: 0.35,
    fronts: 0.45, frontMajor: [8, 14], frontMinor: [3.5, 5.5], frontPeak: [0.35, 0.7], frontLife: [20, 40],
    convective: 0.4, convRadius: [3, 5], convPeak: [0.8, 1], convLife: [4, 9],
    fogChance: 0.2, wetStart: 0.1, snowStart: 0, frozenStart: false,
  },
  autumn: {
    temp: 9, trend: -0.2, diurnal: 4, wind: 0.6,
    fronts: 1.4, frontMajor: [10, 18], frontMinor: [4, 7], frontPeak: [0.5, 1], frontLife: [30, 60],
    convective: 0, convRadius: [2.5, 4], convPeak: [0.6, 0.9], convLife: [4, 8],
    fogChance: 0.55, wetStart: 0.35, snowStart: 0, frozenStart: false,
  },
  winter: {
    temp: -5, trend: 0, diurnal: 3, wind: 0.5,
    fronts: 1.2, frontMajor: [10, 18], frontMinor: [4, 7], frontPeak: [0.45, 0.9], frontLife: [30, 60],
    convective: 0, convRadius: [2.5, 4], convPeak: [0.6, 0.9], convLife: [4, 8],
    fogChance: 0.3, wetStart: 0.3, snowStart: 0.35, frozenStart: true,
  },
};
